import React from 'react';
import { X, Download, Send } from 'lucide-react';
import { Invoice } from '../utils/invoiceUtils';
import { generateInvoicePDF } from '../utils/invoicePdfGenerator';

interface InvoicePreviewProps {
  invoice: Invoice;
  onClose: () => void;
  onSend: () => void;
  isOpen: boolean;
}

const InvoicePreview: React.FC<InvoicePreviewProps> = ({
  invoice,
  onClose,
  onSend,
  isOpen,
}) => {
  if (!isOpen) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleDownload = async () => {
    try {
      // Generate PDF using the dedicated invoice PDF generator
      const pdfBlob = await generateInvoicePDF(invoice);

      // Create download link
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${invoice.invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Invoice Preview</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Header Information */}
            <div className="border-b dark:border-gray-700 pb-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Invoice Number</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{invoice.invoiceNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Issue Date</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{formatDate(invoice.issueDate)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Due Date</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{formatDate(invoice.dueDate)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Status</p>
                  <p className={`inline-flex px-2 py-1 rounded-full text-xs ${
                    invoice.status === 'paid'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : invoice.status === 'overdue'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                  }`}>
                    {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                  </p>
                </div>
              </div>
            </div>

            {/* Student Information */}
            <div className="border-b dark:border-gray-700 pb-4">
              <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">Student Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Student Name</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{invoice.studentName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Student ID</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{invoice.studentId}</p>
                </div>
              </div>
            </div>

            {/* Invoice Items */}
            <div>
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Invoice Items</h3>
              <div className="space-y-4">
                {invoice.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <p className="text-gray-700 dark:text-gray-300">{item.description}</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(item.amount)}</p>
                  </div>
                ))}
                <div className="border-t dark:border-gray-700 pt-4 flex justify-between items-center">
                  <p className="font-semibold text-gray-900 dark:text-gray-100">Total Amount</p>
                  <p className="font-bold text-lg text-gray-900 dark:text-gray-100">{formatCurrency(invoice.totalAmount)}</p>
                </div>
              </div>
            </div>

            {/* Period Information */}
            <div className="border-t dark:border-gray-700 pt-4">
              <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">Period</h3>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">From</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{formatDate(invoice.startDate)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">To</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{formatDate(invoice.endDate)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex justify-end space-x-4">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleDownload}
              className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </button>
            <button
              onClick={onSend}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <Send className="h-4 w-4 mr-2" />
              Send Invoice
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePreview;